import { useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { institutionsAPI } from "../services/api";

interface UserSectionProps {
  portal?: string;
}

export default function UserSection({ portal }: UserSectionProps) {
  const { user, signOut, userRole } = useAuth();
  const rootNavigate = useNavigate();
  const [institutionName, setInstitutionName] = useState<string | null>(null);

  // Fetch institution name for institution admin so sidebar shows it
  useEffect(() => {
    if (!user || userRole !== "institutions_admin") {
      setInstitutionName(null);
      return;
    }
    let cancelled = false;
    institutionsAPI
      .getAll()
      .then((raw: unknown) => {
        if (cancelled) {
          return;
        }
        let list: { name?: string }[] = [];
        if (Array.isArray(raw)) {
          list = raw as { name?: string }[];
        } else if (raw && typeof raw === "object") {
          const inner = (raw as { data?: unknown }).data;
          if (Array.isArray(inner)) {
            list = inner as { name?: string }[];
          } else if (inner && typeof inner === "object") {
            list = [inner as { name?: string }];
          } else {
            list = ((raw as { results?: unknown }).results ?? []) as {
              name?: string;
            }[];
          }
        }
        const first = list[0];
        const name =
          first && typeof first.name === "string" && first.name.trim()
            ? first.name.trim()
            : null;
        setInstitutionName(name);
      })
      .catch(() => {
        if (!cancelled) {
          setInstitutionName(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user, userRole]);

  const handleSignOut = async () => {
    await signOut();

    // Determine redirect based on portal or userRole
    let redirectPortal = portal ?? "student";
    if (!portal) {
      if (userRole === "super_admin" || userRole === "management") {
        redirectPortal = "super_admin";
      } else if (userRole === "institutions_admin") {
        redirectPortal = "institution";
      } else if (userRole === "donor") {
        redirectPortal = "donor";
      } else if (userRole === "trustee") {
        redirectPortal = "trustee";
      }
    }

    rootNavigate(`/jainboarding-admin/auth?portal=${redirectPortal}`);
  };

  const displayName =
    (userRole === "institutions_admin" ? institutionName : null) ??
    user?.full_name ??
    (user as any)?.profile?.full_name ??
    (user?.first_name
      ? `${user.first_name}${user.last_name ? " " + user.last_name : ""}`.trim()
      : null) ??
    (userRole
      ? userRole
          .split("_")
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : (user?.email?.split("@")[0] ?? "User"));
  const userEmail = user?.email ?? "";
  const fallback =
    userRole === "institutions_admin" && institutionName
      ? institutionName.slice(0, 2).toUpperCase()
      : displayName
        ? displayName.slice(0, 2).toUpperCase()
        : "";

  return (
    <div className="mt-auto border-t border-border">
      <div className="rounded-xl p-4 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-primary/10">
              <AvatarFallback className="bg-primary font-bold text-primary-foreground">
                {fallback}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col overflow-hidden">
              <span className="truncate text-base font-bold text-foreground">
                {displayName}
              </span>
              <span className="truncate text-[12px] text-muted-foreground">
                {userEmail}
              </span>
            </div>
          </div>
          {/* <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary">
            <Bell className="h-4 w-4" />
          </Button> */}
        </div>

        <Button
          variant="outline"
          className="mt-4 h-9 w-full justify-center gap-2 rounded-lg border-destructive/20 text-destructive hover:border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
          onClick={handleSignOut}
        >
          <LogOut className="h-4 w-4" />
          <span className="text-sm font-semibold">Logout</span>
        </Button>
      </div>
    </div>
  );
}
